import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { useHelp } from './HelpContext'
import HelpBubble from './HelpBubble'
import { OBSERVED_STEPS, PAGE_TOURS, TIPS, WELCOME_TOUR } from './content'
import { modalOpen, routeOf, visibleElement } from './dom'
import './help.css'

const TICK_MS = 700
// Pace: wait after arriving on a page, and between one tip and the next.
const SETTLE_MS = 2500
const TIP_GAP_MS = 25000

function TourCard() {
  const { tour, endTour, moveTour } = useHelp()
  const step = tour.steps[tour.index]
  const last = tour.index === tour.steps.length - 1
  return (
    <HelpBubble target={step.target} dim label={step.title} onEscape={() => endTour(true)}>
      {tour.steps.length > 1 ? (
        <div className="help-card-count">
          {tour.index + 1} of {tour.steps.length}
        </div>
      ) : null}
      <div className="help-card-title">{step.title}</div>
      <div className="help-card-body">{step.body}</div>
      <div className="help-card-actions">
        <button type="button" className="help-link" onClick={() => endTour(true)}>
          {last ? 'Close' : 'Skip tour'}
        </button>
        <span className="help-card-spacer" />
        {tour.index > 0 ? (
          <button type="button" className="ct-btn ct-btn-ghost" onClick={() => moveTour(-1)}>
            Back
          </button>
        ) : null}
        <button
          type="button"
          className="ct-btn ct-btn-primary"
          onClick={() => (last ? endTour(true) : moveTour(1))}
        >
          {last ? 'Done' : 'Next'}
        </button>
      </div>
    </HelpBubble>
  )
}

function TipCard() {
  const { tip, closeTip, setOff } = useHelp()
  return (
    <HelpBubble target={tip.target} label={tip.title} onEscape={() => closeTip(true)}>
      <div className="help-card-kicker">Tip</div>
      <div className="help-card-title">{tip.title}</div>
      <div className="help-card-body">{tip.body}</div>
      <div className="help-card-actions">
        <button type="button" className="help-link" onClick={() => setOff(true)}>
          Turn off tips
        </button>
        <span className="help-card-spacer" />
        <button type="button" className="ct-btn ct-btn-primary" onClick={() => closeTip(true)}>
          Got it
        </button>
      </div>
    </HelpBubble>
  )
}

// Watches the page and offers help one piece at a time: the welcome tour once,
// each page's tour on the first visit, then tips as their features first
// appear on screen. Also ticks Getting started steps that leave no record.
function HelpLayer() {
  const help = useHelp()
  const { pathname } = useLocation()
  const route = routeOf(pathname)
  const arrived = useRef({ route, at: Date.now() })

  if (arrived.current.route !== route) arrived.current = { route, at: Date.now() }

  const { loaded, seen, steps, off, tour, tip, lastClosed, markStep, startTour, endTour, closeTip, showTip } =
    help

  useEffect(() => {
    if (!loaded) return undefined
    const tick = () => {
      for (const observed of OBSERVED_STEPS) {
        if (observed.route === route && !steps.has(observed.step) && visibleElement(observed.target)) {
          markStep(observed.step)
        }
      }

      if (tour) {
        if (tour.route !== route) endTour(false)
        return
      }
      if (tip) {
        if (modalOpen() || !visibleElement(tip.target)) closeTip(false)
        return
      }
      if (off || modalOpen()) return

      const now = Date.now()
      const settled = now - arrived.current.at > SETTLE_MS && now - lastClosed.current > SETTLE_MS
      if (!settled) return

      if (!seen.has(WELCOME_TOUR.id)) {
        if (visibleElement('.app-navtabs')) startTour(WELCOME_TOUR, route)
        return
      }
      const pageTour = PAGE_TOURS[route]
      if (pageTour && !seen.has(pageTour.id)) {
        const first = pageTour.steps.find((step) => step.target)
        if (!first || visibleElement(first.target)) startTour(pageTour, route)
        return
      }

      if (now - lastClosed.current < TIP_GAP_MS) return
      const next = TIPS.find(
        (entry) => (!entry.route || entry.route === route) && !seen.has(entry.id) && visibleElement(entry.target)
      )
      if (next) showTip(next)
    }
    const id = setInterval(tick, TICK_MS)
    return () => clearInterval(id)
  }, [loaded, route, seen, steps, off, tour, tip, lastClosed, markStep, startTour, endTour, closeTip, showTip])

  if (tour) return <TourCard />
  if (tip) return <TipCard />
  return null
}

export default HelpLayer
